import React, { useMemo, useContext } from "react";
import { useSyncedField, SidebarContext } from "../../components/Sidebar";

const TYPE = {
    PERMISSION: 1, // Read / Add-Modify / Delete
    ASSOCIATIONS: 2, // Link / Unlink
    ACTION: 4, // Execute
};

function entityLabel(et) {
    return et.plural || et.code;
}

const KEYS_BY_TYPE = {
    [TYPE.PERMISSION]: ["read", "modify", "delete"],
    [TYPE.ASSOCIATIONS]: ["link", "unlink"],
    [TYPE.ACTION]: ["execute"],
};

const shapeForType = (t) =>
    Object.fromEntries((KEYS_BY_TYPE[t] || []).map((k) => [k, false]));

const TableStyles = () => (
    <style>{`
      .perm-table { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; }
      .perm-table col.resource { width: auto; }
      .perm-table col.perm { width: 140px; }  /* fixed column width */
      .perm-table thead th { padding: 10px 8px; font-weight: 600; color: #6c757d; }
      .perm-table tbody td { padding: 10px 8px; border-top: 1px solid #eee; }
      .perm-table th.perm, .perm-table td.perm { text-align: center; vertical-align: middle; }
      .perm-table input[type="checkbox"] { display: block; margin: 0 auto; }
    `}</style>
);

const SectionTitle = ({ children }) => (
    <div className="d-flex align-items-center mt-3 mb-1">
        <h6 className="mb-0 text-uppercase text-muted">{children}</h6>
        <hr className="flex-grow-1 ms-2 my-1" />
    </div>
);

const PermissionGroupTable = ({ rows, permissions, togglePerm }) =>
    !rows?.length ? null : (
        <div className="mb-3">
            <TableStyles />
            <SectionTitle>Permissions</SectionTitle>

            <table className="perm-table">
                <colgroup>
                    <col className="resource" />
                    <col className="perm" />
                    <col className="perm" />
                    <col className="perm" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Resource</th>
                        <th className="perm">Read</th>
                        <th className="perm">Add / Modify</th>
                        <th className="perm">Delete</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((et) => (
                        <tr key={et.id}>
                            <td title={et.description}>{entityLabel(et)}</td>
                            <td className="perm">
                                <input
                                    type="checkbox"
                                    checked={Boolean(
                                        permissions[et.code]?.read,
                                    )}
                                    onChange={() => togglePerm(et.code, "read")}
                                    aria-label={`${entityLabel(et)} read`}
                                />
                            </td>
                            <td className="perm">
                                <input
                                    type="checkbox"
                                    checked={Boolean(
                                        permissions[et.code]?.modify,
                                    )}
                                    onChange={() =>
                                        togglePerm(et.code, "modify")
                                    }
                                    aria-label={`${entityLabel(et)} modify`}
                                />
                            </td>
                            <td className="perm">
                                <input
                                    type="checkbox"
                                    checked={Boolean(
                                        permissions[et.code]?.delete,
                                    )}
                                    onChange={() =>
                                        togglePerm(et.code, "delete")
                                    }
                                    aria-label={`${entityLabel(et)} delete`}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

const RelationGroupTable = ({ rows, permissions, togglePerm }) =>
    !rows?.length ? null : (
        <div className="mb-3">
            <TableStyles />
            <SectionTitle>Associations</SectionTitle>
            <table className="perm-table">
                <colgroup>
                    <col className="resource" />
                    <col className="perm" />
                    <col className="perm" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Resource</th>
                        <th className="perm">Link</th>
                        <th className="perm">Unlink</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((et) => (
                        <tr key={et.id}>
                            <td title={et.description}>{entityLabel(et)}</td>
                            <td className="perm">
                                <input
                                    type="checkbox"
                                    checked={Boolean(
                                        permissions[et.code]?.link,
                                    )}
                                    onChange={() => togglePerm(et.code, "link")}
                                    aria-label={`${entityLabel(et)} link`}
                                />
                            </td>
                            <td className="perm">
                                <input
                                    type="checkbox"
                                    checked={Boolean(
                                        permissions[et.code]?.unlink,
                                    )}
                                    onChange={() =>
                                        togglePerm(et.code, "unlink")
                                    }
                                    aria-label={`${entityLabel(et)} unlink`}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

const ActionGroupTable = ({ rows, permissions, togglePerm }) =>
    !rows?.length ? null : (
        <div className="mb-3">
            <TableStyles />
            <SectionTitle>Actions</SectionTitle>
            <table className="perm-table">
                <colgroup>
                    <col className="resource" />
                    <col className="perm" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Resource</th>
                        <th className="perm">Execute</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((et) => (
                        <tr key={et.id}>
                            <td title={et.description}>{entityLabel(et)}</td>
                            <td className="perm">
                                <input
                                    type="checkbox"
                                    checked={Boolean(
                                        permissions[et.code]?.execute,
                                    )}
                                    onChange={() =>
                                        togglePerm(et.code, "execute")
                                    }
                                    aria-label={`${entityLabel(et)} execute`}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

const RolePermissions = () => {
    const { entity } = useContext(SidebarContext);
    const [permissions, setPermissions] = useSyncedField("permissions", {});

    const entityTypes = useMemo(() => {
        return entity?.entityTypes || [];
    }, [entity]);

    const groups = useMemo(() => {
        const g = {
            [TYPE.PERMISSION]: [],
            [TYPE.ASSOCIATIONS]: [],
            [TYPE.ACTION]: [],
        };
        entityTypes.forEach((et) => {
            if (et?.type === TYPE.PERMISSION) g[TYPE.PERMISSION].push(et);
            else if (et?.type === TYPE.ASSOCIATIONS)
                g[TYPE.ASSOCIATIONS].push(et);
            else if (et?.type === TYPE.ACTION) g[TYPE.ACTION].push(et);
        });
        Object.values(g).forEach((arr) =>
            arr.sort((a, b) => entityLabel(a).localeCompare(entityLabel(b))),
        );
        return g;
    }, [entityTypes]);

    const togglePerm = (entityCode, permKey) => {
        setPermissions({
            ...permissions,
            [entityCode]: {
                ...permissions[entityCode],
                [permKey]: !permissions[entityCode]?.[permKey],
            },
        });
    };

    return (
        <div className="p-3">
            <PermissionGroupTable
                rows={groups[TYPE.PERMISSION]}
                permissions={permissions}
                togglePerm={togglePerm}
            />
            <RelationGroupTable
                rows={groups[TYPE.ASSOCIATIONS]}
                permissions={permissions}
                togglePerm={togglePerm}
            />
            <ActionGroupTable
                rows={groups[TYPE.ACTION]}
                permissions={permissions}
                togglePerm={togglePerm}
            />
        </div>
    );
};

export default RolePermissions;
